import type { SupabaseClient } from '@supabase/supabase-js';

export interface ResolvedMedia {
  mediaId: string;
  knowledgeNodeId: string;
  url: string | null;
  partType: string;
  filename: string | null;
}

/**
 * Resolve canonical media row IDs to their primary attachment. Kept for media
 * routes and MCP responses; graph relationships use resolveMediaNodeIds.
 */
export async function resolveMediaIds(
  supabase: SupabaseClient,
  ids: string[],
): Promise<Map<string, ResolvedMedia>> {
  const out = new Map<string, ResolvedMedia>();
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return out;

  const { data, error } = await supabase
    .from('media')
    .select('id, knowledge_node_id, media_parts(part_index, public_url, part_type, filename)')
    .in('id', unique);
  if (error || !data) return out;

  for (const raw of data as Array<{
    id: string;
    knowledge_node_id: string;
    media_parts: Array<{ part_index: number; public_url: string | null; part_type: string; filename: string | null }>;
  }>) {
    const part = [...(raw.media_parts ?? [])].sort((a, b) => a.part_index - b.part_index)[0];
    out.set(raw.id, {
      mediaId: raw.id,
      knowledgeNodeId: raw.knowledge_node_id,
      url: part?.public_url ?? null,
      partType: part?.part_type ?? 'file',
      filename: part?.filename ?? null,
    });
  }
  return out;
}

/** Resolve native media knowledge-node IDs to their canonical media rows. */
export async function resolveMediaNodeIds(
  supabase: SupabaseClient,
  nodeIds: string[],
): Promise<Map<string, ResolvedMedia>> {
  const out = new Map<string, ResolvedMedia>();
  const unique = [...new Set(nodeIds.filter(Boolean))];
  if (unique.length === 0) return out;

  const { data, error } = await supabase
    .from('media')
    .select('id, knowledge_node_id, media_parts(part_index, public_url, part_type, filename)')
    .in('knowledge_node_id', unique);
  if (error || !data) return out;

  for (const raw of data as Array<{
    id: string;
    knowledge_node_id: string;
    media_parts: Array<{ part_index: number; public_url: string | null; part_type: string; filename: string | null }>;
  }>) {
    const part = [...(raw.media_parts ?? [])].sort((a, b) => a.part_index - b.part_index)[0];
    out.set(raw.knowledge_node_id, {
      mediaId: raw.id,
      knowledgeNodeId: raw.knowledge_node_id,
      url: part?.public_url ?? null,
      partType: part?.part_type ?? 'file',
      filename: part?.filename ?? null,
    });
  }
  return out;
}
